import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { ManText } from '@craft-design-system/core-components-react';
import PopoverComponent from '../popover';
import SegmentedControl_totalChargingTime from './SegmentedControl_totalChargingTime';
import useSegmentedChartQuery from '../../utils/useSegmentedChartQuery';
import Shimmer_graph from '../shimmer_graph';
import Zoom from '../Zoom';
import DownloadButtonComponent from '../DownloadButtonComponent';
import { Popover, OverlayTrigger } from 'react-bootstrap';
import ifav from './ifav.png';

const Charger_connected_start_time_and_end_time = ({ value }) => {
  const { vin_short, chargerConnectedStartAndEndTime } = value;

  const [chartType, setChartType] = useState("histogram");
  
  const [charger_connected_time_total, set_charger_connected_time_total] = useState(chargerConnectedStartAndEndTime?.[0] ? JSON.parse(chargerConnectedStartAndEndTime[0]) : { data: [], layout: {} });
  const [downloadData, setDownloadData] = useState(chargerConnectedStartAndEndTime?.[1] ? JSON.parse(chargerConnectedStartAndEndTime[1]) : []);
  const [isLoading, setIsLoading] = useState(false);
  
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { fetchSegmentedChart } = useSegmentedChartQuery();
  const tooltipContent = chartType === "histogram"
    ? `
    <b>Definition</b><br/>
    
    This KPI is visualizing the <b>the start and end time of the charger’s connection to the vehicle.</b>
    <br/>
    <b>Histogram</b><br/>

    Each bar represents the number of "charger connected events" that fall within a certain range (bin) of <b>start and end times of charger connection to the vehicle.</b>
    All start and end time values since start of data acquisition for the selected vehicle(s) are visualized per default.

    `
    : `Boxplot

    The boxplot shows the <b>distribution of start and end times of charger connection</b>  for the selected vehicle(s) since start of data acquisition. If multiple vehicles are selected, the boxplot reflects the combined distribution of all their values.
    <ul>
    <li>The box represents the middle 50% of values (from the 25th percentile ("q1") to the 75th percentile ("q3")).<li>
    <li>The line inside the box shows the median (50th percentile).</li>
    <li>The whiskers extend to show the range of typical values ("min" and "max") and visualize the spread of the data beyond the central box.</li>
    </ul>`;

    const popover = (
      <Popover id="popover-basic">
        <Popover.Body>
          <strong>Definition:</strong><br />
          This KPI displays the start and end time of the charger’s connection to the vehicle.<br /><br />
    
          <strong>Telematic raw data for this KPI:</strong><br />
          TBM3:<br />
          • signal_timestamp<br />
          • highvoltsysmode<br />
          CM4:<br />
          • signal_timestamp<br />
          • highvoltsysmode<br /><br />
    
          <strong>Time series data:</strong><br />
          Please see "Overview charging events" for this insight.<br /><br />
    
          <strong>Histogram:</strong><br />
          Each bar represents the number of "charger connected events" that fall within a certain range (bin) of start and end times of charger connection to the vehicle.<br />
          All start and end time values since start of data acquisition for the selected vehicle(s) are visualized per default.<br /><br />
    
          <strong>Boxplot:</strong><br />
          The boxplot shows the distribution of start and end times of charger connection for the selected vehicle(s) since start of data acquisition.<br />
          If multiple vehicles are selected, the boxplot reflects the combined distribution of all their values.<br />
          • The <strong>box</strong> represents the middle 50% of values (from the 25th percentile ("q1") to the 75th percentile ("q3")).<br />
          • The line <strong>inside the box</strong> shows the median (50th percentile).<br />
          • The whiskers extend to show the range of typical values ("min" and "max") and visualize <strong>the spread of the data</strong> beyond the central box.
        </Popover.Body>
      </Popover>
    );


  // Load fallback data only once
  useEffect(() => {
    if (!charger_connected_time_total && chargerConnectedStartAndEndTime) {
      try {
        const parsed = JSON.parse(chargerConnectedStartAndEndTime);
        set_charger_connected_time_total(parsed);
      } catch (error) {
        console.error('Error parsing fallback data JSON', error);
      }
    }
  }, [chargerConnectedStartAndEndTime, charger_connected_time_total]);

  // Fetch chart and download data
  useEffect(() => {
    const fetchData = async () => {
      if (!vin_short?.length) return;

      setIsLoading(true);
      try {
        const data = await fetchSegmentedChart({
          vinShort: value.vin_short,
          chartType,
          columnname: 'Charger Connected Time',
        });

        set_charger_connected_time_total(JSON.parse(data[0]));   // chart
        setDownloadData(JSON.parse(data[1]));        // download
      } catch (error) {
        console.error('Error fetching chart data:', error);
        set_charger_connected_time_total({ data: [], layout: {} });
        setDownloadData([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isInitialLoad) {
      fetchData();
    } else {
      setIsInitialLoad(false);
    }
  }, [chartType, JSON.stringify(vin_short)]);

  return (
    <div style={{ padding: '16px' }}>
      {/* Title and Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        gap: '8px'
      }}>
        <div style={{
          flex: 1,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <ManText
            className="card-title-text"
            variant="component-l"
            as="span"
            size="l"
            style={{ lineHeight: 'normal' }}
          >
            Charger connected start time and end time
          </ManText>
        </div>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          flexDirection: "row-reverse",
          gap: "8px"
        }}>
          {/* <PopoverComponent
            id="charging_time"
            content={tooltipContent}
          /> */}

          <OverlayTrigger trigger="hover" placement="left" overlay={popover}>
            <img className="man-logovv1" src={ifav} alt="info" style={{ width: '20px', height: '20px', margin: 0 }} />
          </OverlayTrigger>
          <Zoom
            chartData={charger_connected_time_total?.data}
            chartLayout={charger_connected_time_total?.layout}
            chartTitle="Charger connected start time and end time"
          />
          <DownloadButtonComponent
            data={downloadData}
            fileName={`charger_connected_time_${chartType.toLowerCase()}.xlsx`}
            disabled={!downloadData || downloadData.length === 0}
          />
        </div>
      </div>

      {/* Segmented Control */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <SegmentedControl_totalChargingTime
          selected={chartType}
          onChange={setChartType}
          vinShort={Array.isArray(vin_short) ? vin_short : [vin_short]}
          allowedTypes={["histogram", "boxplot"]}
        />
      </div>

      {/* Chart */}
      {isLoading ? (
        <Shimmer_graph />
      ) : charger_connected_time_total?.data && charger_connected_time_total?.layout ? (
        <div style={{ width: '100%', height: '420px', overflow: 'hidden' }}>
          <div style={{ width: '100%', height: '100%' }}>
            <Plot
              data={charger_connected_time_total.data}
              layout={{
                ...charger_connected_time_total.layout,
                autosize: true,
                responsive: true,
                margin: { t: 10, r: 10, l: 10, b: 160 },
              }}
              style={{ width: '100%', height: '100%' }}
              useResizeHandler={true}
            />
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <ManText className='card-title-text' variant='component-l' as='span' size='l'>
            {charger_connected_time_total ? `${charger_connected_time_total} km` : 'No Data Available'}
          </ManText>
        </div>
      )}
    </div>
  );
};

Charger_connected_start_time_and_end_time.propTypes = {
  value: PropTypes.shape({
    chargerConnectedStartAndEndTime: PropTypes.string,
    vin_short: PropTypes.array,
  }).isRequired,
};

export default Charger_connected_start_time_and_end_time;

